/// <reference types="vite-plugin-svgr/client" />
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  SensorDescriptor,
  SensorOptions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { MenuProps, message, Table, TableColumnType } from 'antd';
import { FormInstance } from 'antd/es/form';
import React, { useEffect, useMemo, useState } from 'react';
import 'react-quill/dist/quill.snow.css';

import { updateTask, updateTaskLabels } from '../../api/task.api';
import { deleteTasks, getWorkspaceById } from '../../api/workspace';

import { flushSync } from 'react-dom';
import { queryClient } from '../../queryClient';
import { useCurrentWorkspaceEpics } from '../../serverState/epics';
import { usePlanningSession } from '../../serverState/planning';
import { useUpdateTaskMutation } from '../../serverState/tasks';
import { useGetActualSpaceId, useGetWorkspace } from '../../serverState/workspace';
import Task from '../../types/Task';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup } from '../../types/TaskStatus';
import { EditableCell, TableMetadata } from './cell/EditableCell';
import { EditableRow } from './row/EditableRow';
import './TaskTable.scss';
import { TaskTableDataType, TaskTableProps } from './types/TaskTableTypes';
import { NewTaskButton } from './ui/NewTaskButton';

import { updateSpaceEpicsOrder, updateSpaceTasksOrder } from '../../serverState/helpers/tasksOrderHelper';

import Icon from '@ant-design/icons';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { taskCompletedProperties } from '../../analytics/analyticsHelpers';
import ChevronLeftSvg from '../../assets/icons/chevron-left.svg?react';
import { useScrollRestorationContext } from '../../context/ScrollRestorationContext';
import { useNavigateToTask } from '../../hooks/useTaskNavigation';
import { useViewContext } from '../../pages/Space/Context/ViewContext';
import { Epic } from '../../types/Epic';
import { TaskType } from '../../types/TaskType';
import { WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceViewEntityType } from '../../types/WorkspaceView';
import { Column } from '../../types/WorkspaceViewColumn';
import { DeleteTaskModal } from '../Modal/DeleteTaskModal';
import { MoveSprintTaskToPlanningModal } from '../Modal/MoveSprintTaskToPlanningModal';
import { taskRowMenuItems } from '../UI/Utils/uiUtils';
import { TaskTableColumns } from './TaskTableColumns';

const VIRTUAL_THRESHOLD = 100;
const VIRTUAL_MAX_HEIGHT = 'calc(100vh)';

const TaskTable = React.memo(
  ({
    tableDataSource,
    setTableDataSource,
    tableDataLoaded,
    usersWithAccessToSpace = [],
    rowSelection,
    showHeader,
    className,
    customRowSelection,
    tableClassName,
    isPlanningTable,
    showMultiselect,
    selectedItems,
    setSelectedItems,
    onTasksReorder,
    handleEditEstimationSettings,
    isSubtasksTable,
    onOpenSubtask,
    filterColumns,
  }: TaskTableProps) => {
    const epicsQuery = useCurrentWorkspaceEpics();
    const epics = epicsQuery.data || [];

    const [dataSource, setDataSource] = useState<TaskTableDataType[]>(tableDataSource || []);
    const [isManuallyReordering, setIsManuallyReordering] = useState(false);
    const viewContext = useViewContext();
    const scrollRestorationContext = useScrollRestorationContext();

    useEffect(() => {
      // Don't override local state during manual reordering to prevent glitches
      if (isManuallyReordering && viewContext?.view?.entityType === WorkspaceViewEntityType.epic) {
        return;
      }

      setDataSource(tableDataSource || []);
    }, [tableDataSource, isManuallyReordering]);

    const workspaceQuery = useGetWorkspace();
    const taskStatuses: Record<TaskStatus['id'], TaskStatus> = (
      (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
    ).reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
    const activeSprintId = workspaceQuery.data?.activeSprintId;

    const [messageApi, contextHolder] = message.useMessage();

    const EditableContext = React.createContext<FormInstance | null>(null);

    const framework = workspaceQuery.data?.framework;
    const rowMenuItems: MenuProps['items'] = taskRowMenuItems(framework, viewContext?.view ?? undefined);

    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [showSprintToPlanningModal, setShowSprintToPlanningModal] = useState<boolean>();
    const [rowActionTask, setRowActionTask] = useState<Task | null>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

    const navigateToTask = useNavigateToTask();

    const onRowMenuClicked = async (e: { key: React.Key }, item: Task) => {
      setRowActionTask(item);
      if (e.key === 'delete') {
        setRowActionTask(item);
        setOpenDeleteModal(true);
      }
      if (e.key === 'addToSprint') {
        // get Current workspace
        const workspaceAxiosRes = await getWorkspaceById(item.workspaceId);
        const workspace = workspaceAxiosRes.data;

        // if current workspace doesn't have active sprint show alert saying sprint has not started
        if (!workspace.activeSprintId) {
          void message.info('Sprint has not started yet!');
          return;
        }

        await handleSave({
          ...item,
          sprintId: workspace.activeSprintId,
          planningSessionId: null,
        });

        void message.success(`Task added to ${framework === WorkspaceFramework.scrum ? 'sprint' : 'kanban'}!`);
      } else if (e.key === 'addToPlanning' || e.key === 'move_to_planning') {
        if (isPlanningTable) {
          const newSelectedRowKeysObject = { ...customRowSelection?.selectedRowKeysObject };
          newSelectedRowKeysObject[item.id] = true;
          customRowSelection?.addTaskToSelectedKeys(item);
        } else {
          if (item.sprintId && activeSprintId && item.sprintId === activeSprintId) {
            setRowActionTask(item);
            setShowSprintToPlanningModal(true);
          } else {
            await handleSave({
              ...item,
              planningSessionId: planningSessionQuery?.data?.session?.id
                ? Number(planningSessionQuery.data.session.id)
                : null,
              sprintId: null,
            });

            void message.success('Task added to planning!');
          }
        }
      } else if (e.key === 'remove_from_sprint') {
        updateTaskMutation.mutate({
          taskId: item.id,
          task: { ...item, sprintId: null },
        });

        void message.success(`Task removed from ${framework === WorkspaceFramework.scrum ? 'Sprint' : 'Kanban'}!`);
      } else if (e.key === 'rename') {
        setTimeout(() => {
          flushSync(() => {
            document.querySelector(`[task-edit-id='${Number(item.id)}']`)?.click();
          });
        }, 50);
      }
    };

    const updateTaskMutation = useUpdateTaskMutation();

    const planningSessionQuery = usePlanningSession();

    const handleSave = async (row: Task) => {
      const oldTask = dataSource.find((task) => task.id === row.id);
      // Update local data source so the row fields doesn't jump
      const newDataSource = dataSource.map((task) => {
        if (task.id === row.id) {
          return {
            ...task,
            ...row,
          };
        } else if (task.id === row.parentTaskId) {
          const newParentTask = {
            ...task,
            subtasks: task.subtasks?.map((subtask) => {
              if (subtask.id === row.id) {
                return row;
              }
              return subtask;
            }),
          };
          return newParentTask;
        }
        return task;
      });
      setDataSource(newDataSource);
      setTableDataSource(newDataSource);

      await updateTask(Number(row.id), row);
      if (row.TaskLabels) {
        await updateTaskLabels(Number(row.id), {
          labelIds: row.TaskLabels.map((label) => label.labelId),
        });
      }

      queryClient.setQueryData(['tasks', row.workspaceId], (oldDataSource: Task[]) => {
        return oldDataSource.map((task) => {
          if (task.id === row.id) {
            return row;
          } else if (task.parentTaskId == row.id) {
            return {
              ...task,
              epicId: row.epicId,
            };
          }
          return task;
        });
      });
      if (row.type === TaskType.epic) {
        queryClient.setQueryData(['epics', row.workspaceId], (oldDataSource: Epic[]) => {
          return oldDataSource.map((task) => {
            return task.id === row.id ? row : task;
          });
        });
      }
      if (row.parentTaskId) {
        queryClient.setQueryData(['taskSubtasks', row.parentTaskId], (oldDataSource: Task[]) => {
          return oldDataSource.map((task) => {
            if (task.id === row.id) {
              return row;
            }
            return task;
          });
        });
      }

      // Check if status is completed
      if (oldTask?.statusId !== row.statusId && taskStatuses[row.statusId].group === TaskStatusGroup.completed) {
        analytics.track(AnalyticsEvent.taskCompleted, taskCompletedProperties(row, workspaceQuery.data));
      }
    };

    const moveToPlanning = async (item: Task) => {
      setRowActionTask(item);
      setShowSprintToPlanningModal(true);
    };

    const metadata: TableMetadata = { EditableContext, epics };

    const defaultColumns = TaskTableColumns({
      dataSource,
      usersWithAccessToSpace,
      showMultiselect,
      selectedItems,
      setSelectedItems,
      customRowSelection,
      rowMenuProps: (record: any): MenuProps => ({
        items: rowMenuItems,
        onClick: (e) => onRowMenuClicked(e, record),
      }),
      filterColumns: filterColumns ?? [],
      showMessage: async (message: string) => {
        await messageApi.success(message);
      },
      showSubitems: isSubtasksTable
        ? false
        : viewContext?.view?.entityType === WorkspaceViewEntityType.epic
          ? false
          : viewContext?.view?.columns?.hidden?.includes(Column.subitems)
            ? false
            : true,
    });
    let columns: TableColumnType<Task>[] = defaultColumns.map((col) => {
      if (!('editable' in col && col.editable)) {
        return col;
      }
      return {
        ...col,
        onCell: (record: Task) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          fixed: 'true',
          title: col.title as string,
          handleSave,
          handleOpen: (record: Task) => {
            if (isSubtasksTable) {
              onOpenSubtask?.(record);
            } else {
              scrollRestorationContext?.saveScrollBeforeNavigation();
              record && navigateToTask(record);
            }
          },
          moveToPlanning,
          usersWithAccessToSpace,
          metadata,
          handleEditEstimationSettings,
        }),
      };
    });

    // if in planning
    // we are adding these extra columns to make the padding
    if (tableClassName) {
      columns = [
        {
          title: '',
          dataIndex: 'leftSpace',
          width: rowSelection ? '32px' : '16px',
        },
        ...columns,
        {
          title: '',
          dataIndex: 'rightSpace',
          width: rowSelection ? '32px' : '16px',
        },
      ];
    }

    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
    const sensors: SensorDescriptor<SensorOptions>[] = useSensors(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      useSensor(PointerSensor, {
        activationConstraint: {
          // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
          distance: 5,
          // delay: 100,
          // tolerance: 5,
        },
      })
    );

    // Helper function to find if an ID belongs to a task or subtask
    const findTaskOrSubtask = (tasks: TaskTableDataType[], id: number) => {
      for (const task of tasks) {
        if (task.id === id) {
          return { task, subtask: null, isSubtask: false };
        }
        if (task.subtasks) {
          for (const subtask of task.subtasks) {
            if (subtask.id === id) {
              return { task, subtask, isSubtask: true };
            }
          }
        }
      }
      return { task: null, subtask: null, isSubtask: false };
    };

    // Helper function to handle subtask reordering within the same parent
    const handleSubtaskReordering = async (
      parentTask: TaskTableDataType,
      activeSubtask: Task,
      overSubtask: Task,
      activeId: number,
      overId: number
    ) => {
      if (!parentTask.subtasks) return;

      const activeIndex = parentTask.subtasks.findIndex((s) => s.id === activeId);
      const overIndex = parentTask.subtasks.findIndex((s) => s.id === overId);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      // Reorder subtasks within the parent
      const newSubtasks = arrayMove(parentTask.subtasks, activeIndex, overIndex);

      // Update local state
      const newDataSource = dataSource.map((task) => {
        if (task.id === parentTask.id) {
          return { ...task, subtasks: newSubtasks };
        }
        return task;
      });
      setDataSource(newDataSource);

      // Call existing backend API to update task order (includes subtasks)
      try {
        const allTaskIds = newDataSource.flatMap((task) => [
          task.id,
          ...(task.subtasks?.map((subtask) => subtask.id) || []),
        ]);
        await updateSpaceTasksOrder(Number(actualSpaceId), allTaskIds, activeId, overId);
      } catch (error) {
        console.error('Failed to update subtask order:', error);
        // Revert the local changes if API call fails
        setDataSource(dataSource);
        setTableDataSource(dataSource);
      }
    };

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        const activeId = Number(active.id);
        const overId = Number(over?.id);

        // Find if active and over items are subtasks or parent tasks
        const {
          task: activeParent,
          subtask: activeSubtask,
          isSubtask: activeIsSubtask,
        } = findTaskOrSubtask(dataSource, activeId);
        const {
          task: overParent,
          subtask: overSubtask,
          isSubtask: overIsSubtask,
        } = findTaskOrSubtask(dataSource, overId);

        if (!activeParent || !overParent) {
          return;
        }

        // Case 1: Both are parent tasks - existing logic
        if (!activeIsSubtask && !overIsSubtask) {
          const activeIndex = dataSource.findIndex((i) => i.id === activeId);
          const overIndex = dataSource.findIndex((i) => i.id === overId);

          const newOrderArray = arrayMove(dataSource, activeIndex, overIndex);
          setDataSource(newOrderArray);

          const isBreakingOrder = onTasksReorder && !onTasksReorder(dataSource[activeIndex], dataSource[overIndex]);
          if (isBreakingOrder) {
            return;
          }

          const tasksOrder = newOrderArray.map((task) => task.id);

          if (isPlanningTable) {
            // Handle planning table case
          } else if (viewContext?.view?.entityType === WorkspaceViewEntityType.epic) {
            // Set flag to prevent useEffect from overriding during reorder
            setIsManuallyReordering(true);
            try {
              await updateSpaceEpicsOrder(Number(actualSpaceId), tasksOrder);
            } finally {
              // Reset flag after operation completes (success or failure)
              setTimeout(() => setIsManuallyReordering(false), 100);
            }
          } else {
            await updateSpaceTasksOrder(Number(actualSpaceId), tasksOrder, activeId, overId);
          }
        }
        // Case 2: Both are subtasks within the same parent
        else if (activeIsSubtask && overIsSubtask && activeParent.id === overParent.id) {
          const isBreakingOrder = onTasksReorder && !onTasksReorder(activeSubtask!, overSubtask!);
          if (isBreakingOrder) {
            return;
          }

          await handleSubtaskReordering(activeParent, activeSubtask!, overSubtask!, activeId, overId);
        }
        // Case 3: Moving subtask to different parent or other complex cases
        // For now, we'll prevent these moves by doing nothing
        // TODO: Implement cross-parent subtask moves if needed
      }
    };

    const handleDelete = async (deleteSubtasks: boolean) => {
      try {
        const task = rowActionTask;
        if (!task) return;
        const tasksToDelete: number[] = [
          task.id,
          ...(deleteSubtasks ? (task.subtasks ?? []).map((subtask) => subtask.id) : []),
        ];
        await deleteTasks(task.workspaceId, tasksToDelete);

        setOpenDeleteModal(false);

        setTimeout(async () => {
          queryClient.setQueryData(['epics', task.workspaceId], (oldDataSource: Epic[]) => {
            return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
          });
          queryClient.setQueryData(['tasks', task.workspaceId], (oldDataSource: Task[]) => {
            return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
          });
          if (task.parentTaskId || task.epicId) {
            await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', task.parentTaskId ?? task.epicId] });
          }
          void messageApi.success('Task deleted successfully.');
        }, 50);
      } catch (e) {
        console.error(e);
        void messageApi.error('Failed to delete task');
      }
    };

    const actualSpaceId = useGetActualSpaceId();

    const handleCancelTaskDelete = () => {
      setOpenDeleteModal(false);
    };

    const shouldVirtualize = dataSource.length > VIRTUAL_THRESHOLD;

    // For virtual mode, convert string widths to numbers and ensure all columns have explicit widths
    const virtualColumns = useMemo(() => {
      if (!shouldVirtualize) return columns;
      return columns.map((col) => {
        let width: number;
        if (!col.width) {
          width = 400; // Default for flexible columns like title
        } else if (typeof col.width === 'string') {
          width = parseInt(col.width, 10) || 100;
        } else {
          width = col.width;
        }
        return { ...col, width };
      });
    }, [columns, shouldVirtualize]);

    const tableScrollX = useMemo(() => {
      if (!shouldVirtualize) return undefined;
      return virtualColumns.reduce((sum, col) => sum + ((col.width as number) || 0), 0);
    }, [virtualColumns, shouldVirtualize]);

    // Helper function to get all draggable IDs (parent tasks + subtasks)
    const getAllDraggableIds = (tasks: TaskTableDataType[], expandedRowKeys: number[]): number[] => {
      const allIds: number[] = [];
      tasks.forEach((task) => {
        allIds.push(task.id);
        if (task.subtasks && task.subtasks.length > 0 && expandedRowKeys.includes(task.id)) {
          task.subtasks.forEach((subtask) => {
            allIds.push(subtask.id);
          });
        }
      });
      return allIds;
    };

    return (
      <div className={className} style={shouldVirtualize ? { maxWidth: 'calc(100vw - 260px)' } : undefined}>
        {contextHolder}
        <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={useMemo(() => getAllDraggableIds(dataSource, expandedRowKeys), [dataSource, expandedRowKeys])}
            strategy={verticalListSortingStrategy}
          >
            <Table
              showHeader={showHeader}
              size="small"
              components={components}
              rowClassName={(record) => {
                const isPlanningSelected = customRowSelection?.selectedRowKeysObject[record.id];
                const isBatchSelected = selectedItems?.includes(record.id);

                let selectionClass = '';
                if (isPlanningSelected || isBatchSelected) {
                  selectionClass = 'selected-row';
                }

                return `editable-row h-10 ${selectionClass} ${record.isDisabledInFilter ? 'opacity-50' : ''}`;
              }}
              bordered={false}
              dataSource={dataSource}
              columns={shouldVirtualize ? virtualColumns : columns}
              pagination={false}
              virtual={shouldVirtualize}
              scroll={shouldVirtualize ? { x: tableScrollX, y: VIRTUAL_MAX_HEIGHT } : undefined}
              loading={tableDataLoaded}
              className={`${showHeader ? 'top-table-backlog' : undefined} general-table-customization ${tableClassName}`}
              tableLayout="fixed"
              expandable={{
                childrenColumnName: 'subtasks',
                indentSize: 20,
                defaultExpandAllRows: false,
                expandIconColumnIndex: 2,
                rowExpandable: (record) => Array.isArray(record.subtasks) && record.subtasks.length > 0,
                expandIcon: (props) => {
                  if (
                    viewContext?.view?.entityType === WorkspaceViewEntityType.task &&
                    !viewContext?.view?.columns?.hidden?.includes(Column.subitems) &&
                    Array.isArray(props.record.subtasks) &&
                    props.record.subtasks.length > 0
                  ) {
                    return (
                      <div
                        className="flex items-center justify-center"
                        onClick={(e) => props.onExpand(props.record, e)}
                      >
                        <Icon component={ChevronLeftSvg} rotate={props.expanded ? 270 : 180} />
                      </div>
                    );
                  }
                  return null;
                },
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([...expandedRowKeys, record.id]);
                  } else {
                    setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
                  }
                },
              }}
            />
          </SortableContext>
        </DndContext>
        {!isSubtasksTable && dataSource.length === 0 && (
          <div className={`w-[calc(100vw-260px)] sticky left-0`}>
            {/* TODO: make css transition for when it appears */}
            <div className="h-[32px]">
              <NewTaskButton isTableEmpty={dataSource.length === 0} hasRowSelection={rowSelection != null} />
            </div>
          </div>
        )}

        <DeleteTaskModal
          task={rowActionTask || undefined}
          subtasks={rowActionTask?.subtasks || []}
          open={openDeleteModal}
          onDelete={handleDelete}
          onCancel={handleCancelTaskDelete}
        />
        <MoveSprintTaskToPlanningModal
          open={showSprintToPlanningModal || false}
          onCancel={() => {
            setShowSprintToPlanningModal(false);
          }}
          onOk={() => {
            if (rowActionTask) {
              handleSave({
                ...rowActionTask,
                planningSessionId: planningSessionQuery?.data?.session?.id
                  ? Number(planningSessionQuery.data.session.id)
                  : null,
                sprintId: null,
              });
            }
            setRowActionTask(null);
            setShowSprintToPlanningModal(false);
          }}
          confirmLoading={false}
        />
      </div>
    );
  }
);

export default TaskTable;
